import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Snapshot} from "../models/snpashot.model";
import {OrderSettlement} from "../models/order-settlement.model";
import {OrderSettlementDgSearchResults} from "../models/datagrid-search-results/order-settlement.dg-search-results.model";
import {Order} from "../models/shop/order.model";
import {VetClinic} from "../models/vet-clinic.model";
import {SummarySettlement} from "../models/summary-settlements.model";

/**
 * Serwis warstwy danych zarządzający rozliczeniami finansowymi (Settlements).
 * Obsługuje agregację środków, potrącanie prowizji oraz zlecanie przelewów wychodzących (Payouts).
 */
@Injectable({
  providedIn: 'root'
})
export class OrderSettlementService implements DataGridServiceInterface<Observable<OrderSettlementDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę rozliczeń finansowych do wyświetlenia w tabeli (DataGrid).
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<OrderSettlementDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new OrderSettlementDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new OrderSettlement(), [],
          vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Filtruje widok rozliczeń na podstawie zaawansowanych parametrów (np. status przelewu).
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>,
                   sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<OrderSettlementDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/settlements/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new OrderSettlementDgSearchResults(vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new OrderSettlement(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera historię zmian audytowych dla konkretnego rozliczenia.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }


  /**
   * Pobiera listę transakcji (zamówień) powiązanych z daną kliniką,
   * które nie zostały jeszcze rozliczone i przelane na jej konto bankowe (Unsettled Funds).
   *
   * @param vetcodeId Identyfikator kliniki.
   * @returns Observable emitujący tablicę oczekujących środków.
   */
  showAllNotSettledSettlementForClinic(vetcodeId: number): Observable<OrderSettlement[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements/not-settled/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new OrderSettlement(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zwraca obiekty klinik (w ramach danego klienta), które aktualnie posiadają
   * na swoim subkoncie środki oczekujące na wypłatę (nierozliczone transakcje).
   *
   * @param clientUUID Identyfikator nadrzędnego klienta (Sieci).
   * @returns Observable emitujący tablicę placówek z saldem dodatnim.
   */
  showAllAvailableClinicsWithNotSettleSettlementsForClient(clientUUID: string): Observable<VetClinic[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clinics/${clientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zleca rzeczywiste wykonanie przelewu bankowego do kliniki (Payout Dispatch).
   *
   * @param uuid Identyfikator przygotowanego rozliczenia.
   * @returns Observable potwierdzający rozpoczęcie procesu wysyłki środków.
   */
  dispatch(uuid: string): Observable<OrderSettlement[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements/dispatch/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new OrderSettlement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera zwięzły, agregowany podgląd rozliczeń.
   */
  prepareSummarySettlementForUUID(uuid: string): Observable<OrderSettlement[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements/by-ord-rep/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new SummarySettlement(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Sprawdza aktualny status zleconego przelewu rozliczeniowego (Dispatch Check).
   */
  checkSettlement(uuid: string): Observable<Order> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/disp-check/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera profil rozliczeniowy dla wskazanego zamówienia, aby sprawdzić
   * kiedy i czy w ogóle środki za tę konkretną wizytę trafiły do kliniki.
   */
  byOrderUUID(uuid: any): Observable<OrderSettlement> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/settlements/order/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new OrderSettlement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces walidacji danych
   * dla danego rekordu rozliczenia przed autoryzacją przelewu.
   */
  checkSettlementData(uuid: string): Observable<Order> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/settlements/check/${uuid}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza odświeżenie i ponowne przeliczenie pojedynczych elementów
   * składających się na paczkę rozliczeniową.
   */
  recalculateSettlementItems(uuid: string): Observable<Order> {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/settlements/recalculate/${uuid}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new Order(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
